import { useState, useEffect } from "react";

import { motion } from "framer-motion";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useAuth } from "../contexts/AuthContext";
import { useGradient } from "../contexts/GradientContext";
import { setCookie, getCookie, deleteCookie } from "../utils/cookies";
import { getStripePricing, getStripeUrls } from "../utils/stripe";
import { fetchWeatherAndSetBackground } from "../utils/weatherBackground";

const Register = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    agreeToTerms: false,
  });
  const [isUSA, setIsUSA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [weatherIcon, setWeatherIcon] = useState("");
  const [weatherType, setWeatherType] = useState("");
  const [pricingData, setPricingData] = useState(null);
  const [temperature, setTemperature] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  const { currentGradient } = useGradient();

  const userId = searchParams.get("userId");
  const accountId = searchParams.get("accountId");

  useEffect(() => {
    const planFromUrl = searchParams.get("plan");

    if (planFromUrl) {
      setCookie("selectedPlan", planFromUrl);
      setSelectedPlan(planFromUrl);
      setPricingData(getStripePricing());
    } else {
      const planFromCookie = getCookie("selectedPlan");

      if (planFromCookie) {
        setSelectedPlan(planFromCookie);
        setPricingData(getStripePricing());
      }
    }
  }, [searchParams]);

  const preloadAndSetBackground = (url) => {
    if (!url) return;
    const img = new Image();

    img.onload = () => setBackgroundImage(url);
    img.onerror = () => {};
    img.src = url;
  };

  useEffect(() => {
    let mounted = true;
    const hasNavigator = typeof navigator !== "undefined";

    (async () => {
      await fetchWeatherAndSetBackground({
        setTemperature: (t) => mounted && setTemperature(t),
        setWeatherIcon: (i) => mounted && setWeatherIcon(i),
        setWeatherType: (s) => mounted && setWeatherType(s),
        setIsUSA: (b) => mounted && setIsUSA(b),
        setBackgroundUrl: (url) => mounted && preloadAndSetBackground(url),
        geolocator: hasNavigator ? navigator : undefined,
      });
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");

      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");

      return;
    }

    if (!formData.agreeToTerms) {
      toast.error("Please agree to the terms and conditions");

      return;
    }

    setLoading(true);

    const registrationData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      password: formData.password,
      email: formData.email,
    };

    if (selectedPlan) {
      registrationData.subscriptionPlan = selectedPlan;
    }

    if (userId) {
      registrationData.userId = userId;
    }

    if (accountId) {
      registrationData.accountId = accountId;
    }

    const result = await register(registrationData);

    if (result.success && result.registrationId) {
      deleteCookie("selectedPlan");

      if (selectedPlan) {
        const stripeUrls = getStripeUrls(
          result.registrationId,
          encodeURIComponent(result.email)
        );
        const planData = stripeUrls[selectedPlan.toLowerCase()];
        const stripeUrl = planData?.url || planData;

        window.location.href = stripeUrl;
      } else {
        navigate("/login?registerConfirmation=true");
      }
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{
        background: `linear-gradient(rgba(31, 41, 55, 0.5), rgba(31, 41, 55, 0.5)), ${currentGradient}`,
      }}
    >
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-5xl"
        initial={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.5 }}
      >
        {/* Main Card Container */}
        <div
          className="relative backdrop-blur-sm bg-gray-800/20 rounded-3xl overflow-hidden"
          style={{
            boxShadow:
              "0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 100px rgba(0, 0, 0, 0.3)",
            minHeight: "650px",
          }}
        >
          {/* Background Image - Full width */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${backgroundImage})`,
              filter: "brightness(0.9)",
            }}
          />

          {/* Gradient Overlay - from left to right */}
          <div
            className="absolute inset-0 z-10"
            style={{
              background:
                "linear-gradient(to right, rgba(31, 41, 55, 0.98), rgba(31, 41, 55, 0.9) 30%, rgba(31, 41, 55, 0.7) 50%, rgba(31, 41, 55, 0.3) 70%, transparent 90%)",
            }}
          />

          {/* Content Container */}
          <div className="relative flex z-20">
            {/* Left Side - Register Form */}
            <div
              className="relative px-6 py-12 sm:p-12 flex flex-col justify-center"
              style={{ width: "480px", minHeight: "650px" }}
            >
              {/* Logo - positioned higher and to the left */}
              <div className="absolute top-8 left-6 sm:left-10">
                <div
                  aria-hidden="true"
                  className="inline-flex items-center justify-center w-10 h-10"
                >
                  {!logoError ? (
                    <img
                      alt="Lunova Player Logo"
                      className="w-full h-full object-contain"
                      src="/logo.png"
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-500 to-orange-500 rounded-lg" />
                  )}
                </div>
              </div>

              {/* Form Content */}
              <div className="mt-16 w-full max-w-sm">
                {/* Plan Indicator Badge */}
                {selectedPlan && (
                  <div
                    className={`mb-6 p-3 rounded-lg border ${
                      selectedPlan === "trial"
                        ? "bg-gradient-to-r from-green-600/20 to-emerald-600/20 border-green-500/30"
                        : "bg-gradient-to-r from-purple-600/20 to-blue-600/20 border-purple-500/30"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <i
                            className={`fas ${selectedPlan === "trial" ? "fa-gift" : "fa-crown"} ${
                              selectedPlan === "trial"
                                ? "text-green-400"
                                : "text-purple-400"
                            }`}
                          />
                          <span className="text-sm font-semibold text-white">
                            {selectedPlan === "yearly"
                              ? "Annual Plan"
                              : selectedPlan === "quarterly"
                                ? "Quarterly Plan"
                                : selectedPlan === "trial"
                                  ? "24-Hour Free Trial"
                                  : "Monthly Plan"}
                          </span>
                        </div>
                        {selectedPlan === "yearly" && pricingData && (
                          <div className="ml-6 space-y-1">
                            <div className="text-lg font-bold text-white">
                              {pricingData.yearly.price}/year
                            </div>
                            <div className="text-xs text-gray-300">
                              <span className="line-through text-gray-400">
                                $23.88/year
                              </span>
                              <span className="ml-2 text-green-400 font-semibold">
                                Save 8%
                              </span>
                            </div>
                          </div>
                        )}
                        {selectedPlan === "monthly" && pricingData && (
                          <div className="ml-6">
                            <div className="text-lg font-bold text-white">
                              {pricingData.monthly.price}/month
                            </div>
                            <div className="text-xs text-gray-400">
                              Billed monthly
                            </div>
                          </div>
                        )}
                        {selectedPlan === "quarterly" && pricingData && (
                          <div className="ml-6">
                            <div className="text-lg font-bold text-white">
                              {pricingData.quarterly.price}/quarter
                            </div>
                            <div className="text-xs text-gray-400">
                              Billed every 3 months
                            </div>
                          </div>
                        )}
                        {selectedPlan === "trial" && pricingData && (
                          <div className="ml-6">
                            <div className="text-xs text-gray-300">
                              Then {pricingData.monthly.price}/month after trial
                              ends
                            </div>
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-gray-400">Step 1 of 2</span>
                    </div>
                  </div>
                )}
                <div className="mb-6">
                  {!selectedPlan && (
                    <p
                      className="text-gray-400 text-xs uppercase tracking-wider mb-4"
                      style={{
                        fontFamily: "Open Sans, sans-serif",
                        fontWeight: 400,
                      }}
                    >
                      Start for free
                    </p>
                  )}
                  <h2
                    className="text-3xl font-bold text-white mb-3 whitespace-nowrap"
                    style={{
                      fontFamily: "Open Sans, sans-serif",
                      fontWeight: 700,
                    }}
                  >
                    Create your account.
                  </h2>
                  <p
                    className="text-gray-400"
                    style={{
                      fontFamily: "Open Sans, sans-serif",
                      fontWeight: 400,
                    }}
                  >
                    Already registered?{" "}
                    <button
                      className="text-blue-400 hover:text-blue-300 transition-colors"
                      type="button"
                      onClick={() => window.location.replace("/login")}
                    >
                      Sign in
                    </button>
                  </p>
                </div>

                {/* Register Form */}
                <form className="space-y-4" onSubmit={handleSubmit}>
                  {/* First Name and Last Name */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      required
                      className="w-full px-4 py-3 rounded-lg bg-gray-700/30 backdrop-blur-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600 transition-all"
                      name="firstName"
                      placeholder="First name"
                      style={{
                        fontFamily: "Open Sans, sans-serif",
                        fontWeight: 400,
                      }}
                      type="text"
                      value={formData.firstName}
                      onChange={handleChange}
                    />
                    <input
                      required
                      className="w-full px-4 py-3 rounded-lg bg-gray-700/30 backdrop-blur-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600 transition-all"
                      name="lastName"
                      placeholder="Last name"
                      style={{
                        fontFamily: "Open Sans, sans-serif",
                        fontWeight: 400,
                      }}
                      type="text"
                      value={formData.lastName}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Email Field */}
                  <div className="relative">
                    <input
                      required
                      className="w-full px-4 py-3 pr-10 rounded-lg bg-gray-700/30 backdrop-blur-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600 transition-all"
                      name="email"
                      placeholder="Email"
                      style={{
                        fontFamily: "Open Sans, sans-serif",
                        fontWeight: 400,
                      }}
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                    <i className="fas fa-envelope absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                  </div>

                  {/* Password Field */}
                  <div className="relative">
                    <input
                      required
                      className="w-full px-4 py-3 pr-10 rounded-lg bg-gray-700/30 backdrop-blur-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600 transition-all"
                      minLength="6"
                      name="password"
                      placeholder="Password"
                      style={{
                        fontFamily: "Open Sans, sans-serif",
                        fontWeight: 400,
                      }}
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                    />
                    <button
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <i
                        className={`fas fa-${showPassword ? "eye-slash" : "eye"}`}
                      />
                    </button>
                  </div>

                  {/* Confirm Password Field */}
                  <div className="relative">
                    <input
                      required
                      className="w-full px-4 py-3 pr-10 rounded-lg bg-gray-700/30 backdrop-blur-sm border border-blue-500/50 text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      minLength="6"
                      name="confirmPassword"
                      placeholder="Confirm password"
                      style={{
                        fontFamily: "Open Sans, sans-serif",
                        fontWeight: 400,
                      }}
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                    />
                    <button
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      <i
                        className={`fas fa-${showConfirmPassword ? "eye-slash" : "eye"}`}
                      />
                    </button>
                  </div>

                  {/* Terms and Conditions */}
                  <div className="flex items-center">
                    <input
                      aria-label="Agree to terms and conditions"
                      aria-required="true"
                      checked={formData.agreeToTerms}
                      className="w-4 h-4 rounded bg-gray-700/30 border-gray-600 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                      id="terms-checkbox"
                      name="agreeToTerms"
                      type="checkbox"
                      onChange={handleChange}
                    />
                    <label
                      className="ml-2 text-sm text-gray-400 cursor-pointer"
                      htmlFor="terms-checkbox"
                      style={{
                        fontFamily: "Open Sans, sans-serif",
                        fontWeight: 400,
                      }}
                    >
                      I agree to the{" "}
                      <a
                        aria-label="Read terms and conditions (opens in new tab)"
                        className="text-blue-400 hover:text-blue-300 transition-colors"
                        href="https://lunova.app/dev/tos"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        Terms and Conditions
                      </a>
                    </label>
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                    <button
                      aria-label="Go to sign in page"
                      className="w-full py-3 px-6 rounded-full bg-gray-600/50 text-gray-300 text-center font-semibold hover:bg-gray-600/60 active:scale-95 transition-all duration-200 order-2 sm:order-1"
                      style={{
                        fontFamily: "Open Sans, sans-serif",
                        fontWeight: 600,
                      }}
                      type="button"
                      onClick={() => window.location.replace("/login")}
                    >
                      Sign In
                    </button>
                    <button
                      aria-busy={loading}
                      aria-label="Create Account"
                      className="w-full py-3 px-6 rounded-full bg-blue-500 text-white font-semibold hover:bg-blue-600 active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed order-1 sm:order-2 flex items-center justify-center"
                      disabled={loading}
                      style={{
                        fontFamily: "Open Sans, sans-serif",
                        fontWeight: 600,
                      }}
                      type="submit"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                          <span>Create Account</span>
                        </>
                      ) : (
                        "Create Account"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Right Side - Empty space for image to show through */}
            <div className="flex-1" />
          </div>

          {/* Weather Info Overlays */}
          {temperature !== null && (
            <>
              {/* Temperature in top right */}
              <div className="absolute top-8 right-12 text-right">
                <div className="flex items-center space-x-2">
                  <i
                    className={`fas ${weatherIcon} text-xl text-white drop-shadow-lg`}
                  />
                  <p
                    className="text-2xl font-bold text-white drop-shadow-lg"
                    style={{ fontFamily: "Open Sans, sans-serif" }}
                  >
                    {temperature}°{isUSA ? "F" : "C"}
                  </p>
                </div>
              </div>

              {/* Weather description in bottom right */}
              <div className="absolute bottom-8 right-12 text-right">
                <p
                  className="text-white text-xs font-semibold drop-shadow-lg"
                  style={{ fontFamily: "Open Sans, sans-serif" }}
                >
                  <i className={`fas ${weatherIcon} mr-1 text-xs`} />
                  {weatherType}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Terms and Privacy - Below the card */}
        <div className="mt-6 text-center">
          <p
            className="text-xs text-gray-400"
            style={{ fontFamily: "Open Sans, sans-serif", fontWeight: 400 }}
          >
            By creating an account, you agree to our{" "}
            <a
              className="text-blue-400 hover:text-blue-300 transition-colors"
              href="https://lunova.app/dev/tos"
              rel="noopener noreferrer"
              target="_blank"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              className="text-blue-400 hover:text-blue-300 transition-colors"
              href="https://lunova.app/dev/privacy"
              rel="noopener noreferrer"
              target="_blank"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Register;
